import * as Lockr from 'lockr';
import {
	merge,
	extend,
	assign,
	getAddedAssetFiles,
	getAssetFilesAfterRemoval,
} from './util';
import {
	INITIALIZE_ASSETS__ACTIONTYPE,
	ADD_ASSETS__ACTIONTYPE,
	REMOVE_ASSETS__ACTIONTYPE,
	SWITCH_TO_ASSET__ACTIONTYPE,
	CHANGE_ASSET__ACTIONTYPE,
	SAVE_ASSET__ACTIONTYPE,
	REVERT_ASSET__ACTIONTYPE,
	LOAD_ASSETS__ACTIONTYPES,
	RESET_ASSETS__ACTIONTYPE,
	CLEAR_LOAD_RESULT__ACTIONTYPE,
	IMPORT_ASSETS__ACTIONTYPES,
	VALIDATE_ASSETS__ACTIONTYPES,
	SET_RATER_TYPE__ACTIONTYPE,
} from '../../constants/config-editor';

const ASSETS_KEY: string = 'config_editor_assets';

const INITIAL_STATE: ConfigEditor.AssetState = {
	loaded: null,
	pending: {},
	editing: null,
	result: null,
	hostname: null,
	validation: null,
	raterType: 'SOCOTRA_RATER',
};

function getAssetState(): ConfigEditor.AssetState {
	return Lockr.get<ConfigEditor.AssetState>(ASSETS_KEY, INITIAL_STATE);
}

function saveAssetState(assetState: ConfigEditor.AssetState): ConfigEditor.AssetState {
	Lockr.set(ASSETS_KEY, assetState);
	return assetState;
}

function mergeAndSaveAssetState(assetState: ConfigEditor.AssetState, changes: object): ConfigEditor.AssetState {
	return saveAssetState(merge(assetState, changes));
}

function extendAndSaveAssetState(assetState: ConfigEditor.AssetState, changes: object): ConfigEditor.AssetState {
	return saveAssetState(extend(assetState, changes));
}

function handleInitializeAssets(
	state: ConfigEditor.AssetState,
	data: ConfigEditor.AssetFiles,
): ConfigEditor.AssetState {
	return extendAndSaveAssetState(state, {
		...INITIAL_STATE,
		pending: data,
	});
}

function handleAddAssets(state: ConfigEditor.AssetState, data: ConfigEditor.AddAssetsData) {
	const pending: ConfigEditor.AssetFiles = getAddedAssetFiles(data);
	return mergeAndSaveAssetState(state, {pending});
}

function handleRemoveAssets(
	state: ConfigEditor.AssetState,
	modifying: ConfigEditor.ModificationEntity,
	modification: ConfigEditor.AssetsModification,
) {
	const pending: ConfigEditor.AssetFiles = getAssetFilesAfterRemoval(state.pending, modifying, modification);
	return extendAndSaveAssetState(state, {
		pending,
		editing: null,
	});
}

function handleSwitchToAsset(state: ConfigEditor.AssetState, path: string): ConfigEditor.AssetState {
	return mergeAndSaveAssetState(state, {
		editing: {path, contents: state.pending[path]},
	});
}

function handleChangeAsset(state: ConfigEditor.AssetState, contents: string): ConfigEditor.AssetState {
	return mergeAndSaveAssetState(state, {
		editing: {contents},
	});
}

function handleSaveAsset(state: ConfigEditor.AssetState): ConfigEditor.AssetState {
	return mergeAndSaveAssetState(state, {
		pending: {[state.editing.path]: state.editing.contents},
	});
}

function handleRevertAsset(state: ConfigEditor.AssetState): ConfigEditor.AssetState {
	return mergeAndSaveAssetState(state, {
		editing: { contents: state.pending[state.editing.path]},
	});
}

function handleLoadAssets(state: ConfigEditor.AssetState, data: ConfigEditor.DeployResponse): ConfigEditor.AssetState {
	return extendAndSaveAssetState(state, {
		loaded: data.success ? state.pending : state.loaded,
		result: data,
		hostname: data.hostname,
	});
}

function handleResetAssets(state: ConfigEditor.AssetState): ConfigEditor.AssetState {
	return extendAndSaveAssetState(state, {
		pending: state.loaded,
		editing: resetEditingState(state.editing, state.loaded),
	});
}

function handleClearLoadResult(state: ConfigEditor.AssetState): ConfigEditor.AssetState {
	return mergeAndSaveAssetState(state, {
		result: null,
	});
}

function resetEditingState(
	editing: ConfigEditor.AssetEditorState,
	files: ConfigEditor.AssetFiles,
): ConfigEditor.AssetEditorState {
	if (editing && files[editing.path]) {
		return {
			path: editing.path,
			contents: files[editing.path],
		};
	} else {
		return null;
	}
}

function handleValidateAssets(
	state: ConfigEditor.AssetState,
	validation: ConfigEditor.AssetFiles,
): ConfigEditor.AssetState {
	return extendAndSaveAssetState(state, {validation: assign(state.validation, validation)});
}

function handleAssetsAsyncSuccess(state: ConfigEditor.AssetState, reference: string, data: any) {
	switch (reference) {
		case LOAD_ASSETS__ACTIONTYPES.SUCCESS: return handleLoadAssets(state, data);
		case IMPORT_ASSETS__ACTIONTYPES.SUCCESS: return handleInitializeAssets(state, data);
		case VALIDATE_ASSETS__ACTIONTYPES.SUCCESS: return handleValidateAssets(state, data);
		default: return state;
	}
}

export default function assets(
	state: ConfigEditor.AssetState = getAssetState(),
	action: Action,
): ConfigEditor.AssetState {
	const nextState: ConfigEditor.AssetState = Object.assign({}, state);
	switch (action.type) {
		case 'ASYNC_SUCCESS':
			const {asyncSuccessData: {data}, successReference} = action;
			return handleAssetsAsyncSuccess(nextState, successReference, data);
		case INITIALIZE_ASSETS__ACTIONTYPE:
			return handleInitializeAssets(nextState, action.initializeAssetsData);
		case ADD_ASSETS__ACTIONTYPE:
			return handleAddAssets(nextState, action.addAssetsData);
		case REMOVE_ASSETS__ACTIONTYPE:
			const {modifying, modification} = action.removeAssetsData;
			return handleRemoveAssets(nextState, modifying, modification);
		case SWITCH_TO_ASSET__ACTIONTYPE:
			return handleSwitchToAsset(nextState, action.switchToAssetData.path);
		case CHANGE_ASSET__ACTIONTYPE:
			return handleChangeAsset(nextState, action.changeAssetData.contents);
		case SAVE_ASSET__ACTIONTYPE:
			return handleSaveAsset(nextState);
		case REVERT_ASSET__ACTIONTYPE:
			return handleRevertAsset(nextState);
		case RESET_ASSETS__ACTIONTYPE:
			return handleResetAssets(nextState);
		case CLEAR_LOAD_RESULT__ACTIONTYPE:
			return handleClearLoadResult(nextState);
		case SET_RATER_TYPE__ACTIONTYPE:
			return extendAndSaveAssetState(nextState, {raterType: action.raterType});
		default:
			return nextState;
		}
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./ts/store/reducers/config-editor/assets.ts